import React, { useState, useEffect } from 'react';
import { Calendar as CalIcon, Clock, Send, CheckCircle, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const ScheduleMeeting = () => {
  const { toast } = useToast();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState({});
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [step, setStep] = useState(1);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', subject: '', message: '' });

  useEffect(() => { fetchAvailability(); }, [currentMonth]);

  const fetchAvailability = async () => {
    const m = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
    try {
      const res = await axios.get(`${API_URL}/api/schedule/availability?month=${m}`);
      const map = {};
      res.data.forEach(s => { map[s.date] = s; });
      setAvailability(map);
    } catch (e) { console.error(e); }
  };

  const getDaysInMonth = () => {
    const y = currentMonth.getFullYear(), m = currentMonth.getMonth();
    const first = new Date(y, m, 1).getDay();
    const days = new Date(y, m + 1, 0).getDate();
    return { first, days };
  };

  const formatDate = (day) => {
    const y = currentMonth.getFullYear(), m = currentMonth.getMonth();
    return `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const isAvailable = (day) => {
    const date = formatDate(day);
    return availability[date]?.available === true;
  };

  const isPast = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date < new Date(new Date().setHours(0, 0, 0, 0));
  };

  const getSlot = (date) => selectedSlots.find(s => s.date === date);

  const toggleDate = (day) => {
    const date = formatDate(day);
    if (!isAvailable(day) || isPast(day)) return;

    const existing = getSlot(date);
    if (existing) {
      setSelectedSlots(selectedSlots.filter(s => s.date !== date));
    } else if (selectedSlots.length < 3) {
      setSelectedSlots([...selectedSlots, { date, time_slots: [] }]);
    } else {
      toast({ variant: 'destructive', title: 'Maximum 3 dates', description: 'Remove a date to select another.' });
    }
  };

  const toggleTime = (date, time) => {
    setSelectedSlots(selectedSlots.map(s => {
      if (s.date !== date) return s;
      const has = s.time_slots.includes(time);
      if (has) return { ...s, time_slots: s.time_slots.filter(t => t !== time) };
      if (s.time_slots.length >= 3) return s;
      return { ...s, time_slots: [...s.time_slots, time].sort() };
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validSlots = selectedSlots.filter(s => s.time_slots.length > 0);
    if (validSlots.length === 0) {
      toast({ variant: 'destructive', title: 'Error', description: 'Select at least 1 date with time slots.' });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/schedule/request`, {
        ...form, preferred_slots: validSlots
      });
      setDone(true);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Error', description: error.response?.data?.detail || 'Failed' });
    } finally { setSending(false); }
  };

  const { first, days } = getDaysInMonth();
  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));

  if (done) {
    return (
      <div className="min-h-screen bg-white pt-20">
        <div className="container mx-auto px-6 py-32 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-3xl font-extrabold text-[#2B2D42] mb-4">Meeting Request Sent</h2>
          <p className="text-[#6C757D] max-w-md mx-auto mb-8">We will review your preferred dates and confirm a meeting within 48 business hours.</p>
          <Button onClick={() => window.location.href = '/'} variant="outline" className="rounded-xl">Back to Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero — extends behind the transparent fixed header on this route */}
      <section className="bg-[#023E8A] text-white pt-36 pb-16">
        <div className="container mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-10 bg-[#48CAE4]" /><p className="text-xs font-bold uppercase tracking-[0.25em] text-[#48CAE4]">Schedule a Meeting</p><div className="h-px w-10 bg-[#48CAE4]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 tracking-tight">Book Your Meeting</h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto font-light">Select your preferred dates and times. We will confirm availability within 48 hours.</p>
        </div>
      </section>

      <div className="container mx-auto px-6 py-12">
        {/* Steps */}
        <div className="flex items-center justify-center gap-4 mb-12">
          {[{ n: 1, label: 'Select Dates' }, { n: 2, label: 'Choose Times' }, { n: 3, label: 'Your Details' }].map((s, i) => (
            <React.Fragment key={s.n}>
              <button onClick={() => { if (s.n < step || (s.n === 2 && selectedSlots.length > 0) || (s.n === 1)) setStep(s.n); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${step === s.n ? 'bg-[#FF6B35] text-white shadow-lg' : step > s.n ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs">{step > s.n ? '✓' : s.n}</span>
                {s.label}
              </button>
              {i < 2 && <div className={`w-12 h-0.5 ${step > s.n ? 'bg-green-400' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Calendar */}
        {step === 1 && (
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="bg-[#023E8A] text-white px-6 py-4 flex items-center justify-between">
                <button onClick={prevMonth} className="hover:bg-white/10 p-2 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
                <h3 className="text-lg font-bold">{MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}</h3>
                <button onClick={nextMonth} className="hover:bg-white/10 p-2 rounded-lg"><ChevronRight className="w-5 h-5" /></button>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {DAYS.map(d => <div key={d} className="text-center text-xs font-bold text-[#6C757D] py-2">{d}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {Array(first).fill(null).map((_, i) => <div key={`e-${i}`} />)}
                  {Array.from({ length: days }, (_, i) => i + 1).map(day => {
                    const date = formatDate(day);
                    const avail = isAvailable(day);
                    const past = isPast(day);
                    const selected = !!getSlot(date);
                    return (
                      <button key={day} onClick={() => toggleDate(day)} disabled={past || !avail}
                        className={`aspect-square rounded-xl text-sm font-semibold transition-all duration-200 relative
                          ${past ? 'text-gray-300 cursor-not-allowed' :
                            selected ? 'bg-[#FF6B35] text-white shadow-lg scale-105' :
                            avail ? 'bg-green-50 text-green-700 hover:bg-green-100 hover:scale-105 border border-green-200 cursor-pointer' :
                            'text-gray-300 cursor-not-allowed'}`}>
                        {day}
                        {avail && !past && !selected && <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-green-500" />}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-4 mt-6 text-xs text-[#6C757D]">
                  <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-green-50 border border-green-200" /> Available</div>
                  <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-[#FF6B35]" /> Selected</div>
                  <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-gray-100" /> Unavailable</div>
                </div>
              </div>
            </div>
            {selectedSlots.length > 0 && (
              <div className="mt-6 text-center">
                <p className="text-sm text-[#6C757D] mb-3">{selectedSlots.length}/3 dates selected</p>
                <Button onClick={() => setStep(2)} className="bg-[#FF6B35] hover:bg-[#E5562D] rounded-xl px-8">Continue to Time Selection</Button>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Time Slots */}
        {step === 2 && (
          <div className="max-w-2xl mx-auto space-y-6">
            <p className="text-center text-[#6C757D] mb-4">Select up to 3 preferred times for each date.</p>
            {selectedSlots.map((slot, idx) => {
              const avail = availability[slot.date];
              const times = avail?.time_slots || ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00"];
              return (
                <div key={slot.date} className="bg-white rounded-2xl border border-gray-100 shadow-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#FF6B35] rounded-xl flex items-center justify-center text-white font-bold">{idx + 1}</div>
                      <div>
                        <p className="font-bold text-[#2B2D42]">{new Date(slot.date + 'T12:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                        <p className="text-xs text-[#6C757D]">{slot.time_slots.length}/3 times selected</p>
                      </div>
                    </div>
                    <button onClick={() => setSelectedSlots(selectedSlots.filter(s => s.date !== slot.date))}><X className="w-4 h-4 text-gray-400 hover:text-red-500" /></button>
                  </div>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                    {times.map(time => {
                      const sel = slot.time_slots.includes(time);
                      return (
                        <button key={time} onClick={() => toggleTime(slot.date, time)}
                          className={`py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${sel ? 'bg-[#023E8A] text-white shadow-md' : 'bg-gray-50 text-[#2B2D42] hover:bg-[#E0F2FE] border border-gray-200'}`}>
                          <Clock className="w-3 h-3 inline mr-1" />{time}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-xl">Back</Button>
              <Button onClick={() => setStep(3)} className="bg-[#FF6B35] hover:bg-[#E5562D] rounded-xl px-8"
                disabled={selectedSlots.every(s => s.time_slots.length === 0)}>Continue</Button>
            </div>
          </div>
        )}

        {/* Step 3: Details */}
        {step === 3 && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-[#f8f9fa] rounded-2xl p-6 mb-8">
              <h4 className="font-bold text-[#2B2D42] mb-3">Your Selected Slots</h4>
              {selectedSlots.filter(s => s.time_slots.length > 0).map((s, i) => (
                <div key={i} className="flex items-center gap-3 text-sm mb-2">
                  <span className="w-6 h-6 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-xs font-bold">{i + 1}</span>
                  <span className="font-medium text-[#2B2D42]">{new Date(s.date + 'T12:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                  <span className="text-[#6C757D]">at {s.time_slots.join(', ')}</span>
                </div>
              ))}
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-semibold text-[#2B2D42] mb-2">Full Name *</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00B4D8]" /></div>
                <div><label className="block text-sm font-semibold text-[#2B2D42] mb-2">Email *</label>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00B4D8]" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-semibold text-[#2B2D42] mb-2">Company</label>
                  <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00B4D8]" /></div>
                <div><label className="block text-sm font-semibold text-[#2B2D42] mb-2">Phone</label>
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00B4D8]" /></div>
              </div>
              <div><label className="block text-sm font-semibold text-[#2B2D42] mb-2">Subject</label>
                <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00B4D8]" /></div>
              <div><label className="block text-sm font-semibold text-[#2B2D42] mb-2">Message</label>
                <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#00B4D8] resize-none" /></div>
              <div className="flex justify-between">
                <Button type="button" variant="outline" onClick={() => setStep(2)} className="rounded-xl">Back</Button>
                <Button type="submit" disabled={sending} className="bg-[#FF6B35] hover:bg-[#E5562D] rounded-xl px-10">
                  {sending ? 'Submitting...' : <><Send className="w-4 h-4 mr-2" /> Submit Request</>}
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleMeeting;
